import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';

interface LPRegenerateSectionPopoverProps {
  componentId: string;
  onRegenerate: (componentId: string, instruction: string) => Promise<void>;
  onCancel: () => void;
}

const LPRegenerateSectionPopover: React.FC<LPRegenerateSectionPopoverProps> = ({
  componentId,
  onRegenerate,
  onCancel,
}) => {
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only dismiss on a backdrop click that BOTH started and ended on the overlay
  // itself — otherwise a text drag-select that releases outside the popover would
  // close it (the click event bubbles to the overlay as the common ancestor).
  const pressStartedOnOverlay = useRef(false);

  const handleRegenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      await onRegenerate(componentId, instruction.trim());
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail
          ? JSON.stringify(detail)
          : err?.message || 'Regeneration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Rendered through a portal so the overlay is fixed to the viewport,
  // unaffected by drag-and-drop transforms on the section ancestors.
  return createPortal(
    <div
      className="lp-regen-popover-overlay"
      onMouseDown={(e) => { pressStartedOnOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        e.stopPropagation();
        // Close only when the click both started and ended on the backdrop itself.
        if (!loading && pressStartedOnOverlay.current && e.target === e.currentTarget) {
          onCancel();
        }
        pressStartedOnOverlay.current = false;
      }}
    >
      <div className="lp-regen-popover" onClick={(e) => e.stopPropagation()}>
        <p className="lp-regen-popover-label">Regenerate section with AI</p>
        <textarea
          className="lp-regen-popover-textarea"
          placeholder="Optional: tell the AI what to change, e.g. 'make this punchier, mention same-day service'"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          disabled={loading}
          autoFocus
          rows={4}
        />
        {error && (
          <p className="lp-regen-popover-error">{error}</p>
        )}
        <div className="lp-regen-popover-actions">
          <GlassButton
            variant="primary"
            size="md"
            onClick={handleRegenerate}
            disabled={loading}
            icon={loading ? <Loader2 size={16} className="spin" /> : undefined}
          >
            {loading ? 'Regenerating…' : 'Regenerate'}
          </GlassButton>
          <GlassButton
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </GlassButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LPRegenerateSectionPopover;
